import { useMemo, useSyncExternalStore } from 'react';

import type {
  AssistantAction,
  AssistantBooking,
  AssistantOperationStatus,
} from './api';

// MEN-5598: a book, reschedule or cancel response can be lost after Moments
// accepted the change. Each command is recorded in sessionStorage before it is
// sent, so a reload finishes it instead of building a second one:
//
// - `submitting`: written before the first send. Whoever finds it sends the
//   stored bytes with the stored idempotency key, so a replay after a lost
//   response gets the original operation back, never a command rebuilt from
//   refreshed versions.
// - `pending`: Moments accepted it; poll the operation.
// - `settled`: there is an answer to show. Only a settled record lets the page
//   build another command for that appointment (or, for a book, that coach).
//
// Records are cleared on sign-out and when the session ends with a 401.

const PREFIX = 'assistant-op:';

/** Tells this page load's unanswered sends from an earlier load's. */
export const PAGE_LOAD_ID = `${Date.now()}-${Math.random()}`;

/** A definitive answer without an operation: a 4xx error code. */
export interface AssistantOperationRefusal {
  error: string;
}

export interface AssistantOperationOutcome {
  declineCode: string | null;
  /** The occurrence's start after the change (or the first booked one), when
   * Moments returned it. */
  startAt: string | null;
  status: Exclude<AssistantOperationStatus, 'pending'>;
}

/** What every record carries, and all a driver step changes. */
export interface AssistantOperationRecordBase {
  idempotencyKey: string;
  memberId: string;
  operationId: string | null;
  outcome: AssistantOperationOutcome | AssistantOperationRefusal | null;
  /** The exact body sent, and re-sent on replay. */
  requestBytes: string;
  retryAfterSeconds: number | null;
  state: 'pending' | 'settled' | 'submitting';
  /** What the command is about, e.g. "Coaching session on Tuesday, Nov 3". */
  summary: string;
  /**
   * The page load whose last send got no answer (network or 5xx). The record
   * stays `submitting`; that page waits for "Try again", and any later page
   * load replays it at once. A replay with the same key is safe.
   */
  unansweredInPage?: string;
}

/** A reschedule or cancel of one existing occurrence. */
export interface AssistantAppointmentOperationRecord extends AssistantOperationRecordBase {
  action: AssistantAction;
  appointmentId: string;
}

/** A new booking with one coach. One unsettled booking per coach at a time. */
export interface AssistantBookOperationRecord extends AssistantOperationRecordBase {
  action: 'book';
  booking: AssistantBooking;
  coachFirstName: string;
  coachPersonId: string;
  /** The start that was asked for. */
  startAt: string;
}

export type AssistantOperationRecord =
  AssistantAppointmentOperationRecord | AssistantBookOperationRecord;

/** What a record is about: an appointment, or a coach for a booking. */
export type AssistantOperationTarget =
  | Pick<
      AssistantAppointmentOperationRecord,
      'action' | 'appointmentId' | 'memberId'
    >
  | Pick<AssistantBookOperationRecord, 'action' | 'coachPersonId' | 'memberId'>;

export const assistantOperationKey = (target: AssistantOperationTarget) =>
  target.action === 'book'
    ? `${PREFIX}${target.memberId}:${target.coachPersonId}:book`
    : `${PREFIX}${target.memberId}:${target.appointmentId}:${target.action}`;

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const hasTarget = (record: Record<string, unknown>): boolean => {
  if (record.action === 'cancel' || record.action === 'reschedule') {
    return typeof record.appointmentId === 'string';
  }
  return (
    record.action === 'book' &&
    (record.booking === 'chemistry' ||
      record.booking === 'series' ||
      record.booking === 'single') &&
    typeof record.coachPersonId === 'string' &&
    typeof record.coachFirstName === 'string' &&
    typeof record.startAt === 'string'
  );
};

const isRecord = (value: unknown): value is AssistantOperationRecord => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    hasTarget(record) &&
    typeof record.memberId === 'string' &&
    typeof record.idempotencyKey === 'string' &&
    typeof record.requestBytes === 'string' &&
    typeof record.summary === 'string' &&
    (record.state === 'submitting' ||
      record.state === 'pending' ||
      record.state === 'settled')
  );
};

const storedKeys = (): string[] => {
  const store = storage();
  if (!store) return [];
  try {
    return Array.from({ length: store.length }, (_, index) => store.key(index))
      .filter((key): key is string => !!key && key.startsWith(PREFIX))
      .sort();
  } catch {
    return [];
  }
};

const readRecord = (key: string): AssistantOperationRecord | null => {
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) && assistantOperationKey(parsed) === key
      ? parsed
      : null;
  } catch {
    return null;
  }
};

/** False when storage refused it: then the command must not be sent. */
export const writeAssistantOperation = (
  record: AssistantOperationRecord,
): boolean => {
  let written = true;
  try {
    const store = storage();
    if (!store) return false;
    store.setItem(assistantOperationKey(record), JSON.stringify(record));
  } catch {
    // A command that can't be recorded can't be recovered after a lost
    // response, so it isn't sent at all.
    written = false;
  }
  notify();
  return written;
};

export const removeAssistantOperation = (
  record: AssistantOperationTarget,
): void => {
  try {
    storage()?.removeItem(assistantOperationKey(record));
  } catch {
    // Nothing to remove without storage.
  }
  notify();
};

let generation = 0;

/** Bumped whenever every record is cleared; a driver loop of an older
 * generation stops. */
export const assistantOperationsGeneration = (): number => generation;

export const clearAssistantOperations = (): void => {
  generation += 1;
  storedKeys().forEach((key) => {
    try {
      storage()?.removeItem(key);
    } catch {
      // Nothing to remove without storage.
    }
  });
  notify();
};

export const readAssistantOperations = (
  memberId: string,
): AssistantOperationRecord[] =>
  storedKeys()
    .map(readRecord)
    .filter(
      (record): record is AssistantOperationRecord =>
        record !== null && record.memberId === memberId,
    );

/** True while a command for this appointment has no answer yet. */
export const hasUnsettledAssistantOperation = (
  records: AssistantOperationRecord[],
  appointmentId: string,
): boolean =>
  records.some(
    (record) =>
      record.action !== 'book' &&
      record.appointmentId === appointmentId &&
      record.state !== 'settled',
  );

/** True while a booking with this coach has no answer yet. */
export const hasUnsettledAssistantBooking = (
  records: AssistantOperationRecord[],
  coachPersonId: string,
): boolean =>
  records.some(
    (record) =>
      record.action === 'book' &&
      record.coachPersonId === coachPersonId &&
      record.state !== 'settled',
  );

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The member's records, re-rendering when any record changes. */
export const useAssistantOperations = (
  memberId: string,
): AssistantOperationRecord[] => {
  // The snapshot must be stable between changes, so it is the raw stored
  // text; the parsed list is derived from it.
  const snapshot = useSyncExternalStore(
    subscribe,
    () =>
      JSON.stringify(
        storedKeys()
          .filter((key) => key.startsWith(`${PREFIX}${memberId}:`))
          .map((key) => storage()?.getItem(key) ?? ''),
      ),
    () => '[]',
  );
  return useMemo(
    () => (snapshot === '[]' ? [] : readAssistantOperations(memberId)),
    [memberId, snapshot],
  );
};
